import {useRequiredConfig} from '@domain/shifthandover'
import {filterSxStyles} from '@hconnect/common/styles'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {FilterList as FilterIcon} from '@mui/icons-material'
import {Badge, Button, Box, useMediaQuery, Theme} from '@mui/material'
import {
  sortingFilterParams,
  desktopFilterOptions,
  defaultDesktop,
  AllDesktopFilterOptionsKeys,
  AllFilterOptions
} from '@widgets/shift-event-filters'
import {isEqual, omit} from 'lodash'
import React, {ReactNode, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

import {ShiftEventsFilter} from './ShiftEventsFilter'

type Props = {
  isCollapsible: boolean
  activeSettings: AllFilterOptions
  onChange: (next: AllFilterOptions) => void
  openMobileFilter: (state: boolean) => void
  isMobileFilterOpen: boolean
  additionalActions?: ReactNode
  showDescriptions?: (show: boolean) => void
  plantId: string
  options?: AllDesktopFilterOptionsKeys[]
  defaultFilters?: string[]
}

export const ShiftEventsFilterBar: React.FC<Props> = (props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const {shifts, kilnStoppageCodes, rawMillStoppageCodes, finishMillStoppageCodes} =
    useRequiredConfig(props.plantId)
  const timezone = useUpmPlantTimezone(props.plantId)
  const getPlantNow = useUpmPlantNow(props.plantId)

  const options = props.options ?? desktopFilterOptions
  const defaultFilters = props.defaultFilters ?? defaultDesktop

  const [settings, setSettings] = useState<AllFilterOptions>(props.activeSettings)

  // sync with the given props
  useMemo(() => setSettings(props.activeSettings), [props.activeSettings])

  if (props.isCollapsible && !props.isMobileFilterOpen) {
    const filters = omit(settings, sortingFilterParams)
    const totalFilters: number = Object.keys(filters).reduce((acc, curr) => {
      if (Array.isArray(settings[curr])) {
        acc += settings[curr].length
      } else if (settings[curr]) {
        acc += 1
      }
      return acc
    }, 0)
    return (
      <Box data-test-id={'events-filter-bar'} sx={filterSxStyles.filterBar}>
        <Box sx={filterSxStyles.filterButton}>
          <Badge
            badgeContent={totalFilters}
            color="primary"
            data-test-id="events-filters-badge"
            overlap="circular"
          >
            <Button
              variant="text"
              startIcon={!isMobile && <FilterIcon />}
              sx={filterSxStyles.filterSearchButton}
              onClick={() => {
                props.openMobileFilter(true)
              }}
              data-test-id="events-filter-bar-toggle-button"
              id="events-filter-bar-toggle-button"
            >
              {isMobile ? <FilterIcon /> : t('shiftEvent.action.filter')}
            </Button>
          </Badge>
        </Box>
      </Box>
    )
  }

  const update = (next: AllFilterOptions) => {
    if (isEqual(settings, next)) {
      return
    }

    if (props.isCollapsible) {
      setSettings(next)
    } else {
      props.onChange(next)
    }
  }

  return (
    <Box
      data-test-id="events-filter-bar-box"
      sx={props.isMobileFilterOpen ? filterSxStyles.filterLayover : filterSxStyles.filterBar}
      display={'flex'}
      flexDirection={'column'}
    >
      {props.isCollapsible && (
        <Box sx={filterSxStyles.filterButton} mr={1} textAlign={'right'}>
          <Button
            data-test-id="events-filter-bar-cancel-button"
            id="events-filter-bar-cancel-button"
            variant={'contained'}
            onClick={() => {
              props.openMobileFilter(false)
              setSettings(props.activeSettings)
            }}
          >
            {t('action.cancel')}
          </Button>
        </Box>
      )}
      <ShiftEventsFilter
        onChange={update}
        settings={settings}
        isCollapsible={props.isCollapsible}
        applyFilter={(filterSettings) => {
          props.openMobileFilter(false)
          props.onChange(filterSettings)
        }}
        options={options}
        defaultFilters={defaultFilters}
        additionalActions={props.additionalActions}
        showDescriptions={props.showDescriptions}
        plantId={props.plantId}
        shifts={shifts}
        timezone={timezone}
        plantNow={getPlantNow()}
        kilnStoppageCodes={kilnStoppageCodes}
        rawMillStoppageCodes={rawMillStoppageCodes}
        finishMillStoppageCodes={finishMillStoppageCodes}
      />
    </Box>
  )
}
